import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {ORG_WEB_CRAWLS} from '../graphql/queries';
import {START_WEB_CRAWL} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function statusColor(status: string): 'default' | 'info' | 'success' | 'error' {
  switch (status) {
    case 'QUEUED': return 'default';
    case 'RUNNING': return 'info';
    case 'SUCCEEDED': return 'success';
    case 'FAILED': return 'error';
    default: return 'default';
  }
}

export default function WebCrawlsPage() {
  useDocumentTitle('Web Crawls');

  const {user} = useAuth();
  const {activeOrg} = useOrg();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [seedUrl, setSeedUrl] = useState('');
  const [maxDepth, setMaxDepth] = useState(3);
  const [maxPages, setMaxPages] = useState(500);
  const [skipExistingUrls, setSkipExistingUrls] = useState(true);
  const [skipClassification, setSkipClassification] = useState(false);

  const {loading, error, data, refetch} = useQuery(ORG_WEB_CRAWLS, {
    variables: {orgId: activeOrg?.id},
    skip: !user || !activeOrg?.id,
    fetchPolicy: 'network-only',
    pollInterval: 5000,
  });

  const [startCrawl, {loading: starting}] = useMutation(START_WEB_CRAWL, {
    refetchQueries: [{query: ORG_WEB_CRAWLS, variables: {orgId: activeOrg?.id}}],
  });

  const activeOrgId = activeOrg?.id;
  useEffect(() => {
    if (activeOrgId && user) refetch();
  }, [activeOrgId, user, refetch]);

  const crawls = data?.orgWebCrawls || [];

  const handleStartCrawl = async () => {
    if (!activeOrg?.id || !seedUrl.trim()) return;
    try {
      const result = await startCrawl({
        variables: {
          orgId: activeOrg.id,
          seedUrl: seedUrl.trim(),
          maxDepth,
          maxPages,
          skipExistingUrls,
          skipClassification,
        },
      });
      setDialogOpen(false);
      setSeedUrl('');
      // Navigate to the job detail page
      const jobId = result.data?.startWebCrawl?.jobId;
      if (jobId) navigate(`/org/jobs/${jobId}`);
    } catch (err) {
      console.error('Failed to start crawl:', err);
    }
  };

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
            Web Crawls
          </Typography>
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            Crawl websites to discover and import documents
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => { setSeedUrl(activeOrg?.website || ''); setDialogOpen(true); }}>
          New Crawl
        </Button>
      </Box>

      {/* List card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          {[
            {label: 'Seed URL', flex: 1, width: undefined},
            {label: 'Pages', flex: undefined, width: 80},
            {label: 'Docs Found', flex: undefined, width: 100},
            {label: 'Imported', flex: undefined, width: 80},
            {label: 'Status', flex: undefined, width: 100},
            {label: 'Created', flex: undefined, width: 120},
          ].map(col => (
            <Typography
              key={col.label}
              sx={{
                flex: col.flex,
                width: col.width,
                fontSize: 12,
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Body */}
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{m: 2}}>
            Error loading crawls: {error.message}
          </Alert>
        )}

        {!loading && !error && crawls.length === 0 && (
          <Box sx={{py: 6, textAlign: 'center'}}>
            <Typography sx={{fontSize: 14, color: '#9CA3AF'}}>
              No web crawls yet. Click "New Crawl" to get started.
            </Typography>
          </Box>
        )}

        {crawls.map((crawl: any) => (
          <Box
            key={crawl.id}
            onClick={() => navigate(`/org/crawls/${crawl.id}`)}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #F3F4F6',
              cursor: 'pointer',
              '&:hover': {bgcolor: '#F9FAFB'},
              '&:last-child': {borderBottom: 'none'},
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: 14,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 2,
              }}
            >
              {crawl.seedUrl}
            </Typography>
            <Typography sx={{width: 80, fontSize: 13, color: '#6B7280'}}>
              {crawl.pagesCrawled}
            </Typography>
            <Typography sx={{width: 100, fontSize: 13, color: '#6B7280'}}>
              {crawl.documentsFound}
            </Typography>
            <Typography sx={{width: 80, fontSize: 13, color: '#6B7280'}}>
              {crawl.documentsImported}
            </Typography>
            <Box sx={{width: 100}}>
              <Chip
                label={crawl.status}
                size="small"
                color={statusColor(crawl.status)}
                variant={crawl.status === 'RUNNING' ? 'filled' : 'outlined'}
                sx={{height: 22, fontSize: 11}}
              />
            </Box>
            <Typography sx={{width: 120, fontSize: 13, color: '#9CA3AF'}}>
              {formatRelativeTime(crawl.createdAt)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* New Crawl Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Web Crawl</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Website URL"
            placeholder="https://example.com/docs"
            fullWidth
            value={seedUrl}
            onChange={e => setSeedUrl(e.target.value)}
            sx={{mt: 1, mb: 2}}
          />
          <Box sx={{display: 'flex', gap: 2}}>
            <TextField
              label="Max Depth"
              type="number"
              value={maxDepth}
              onChange={e => setMaxDepth(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
              inputProps={{min: 1, max: 10}}
              size="small"
              sx={{width: 120}}
            />
            <TextField
              label="Max Pages"
              type="number"
              value={maxPages}
              onChange={e => setMaxPages(Math.max(1, Math.min(20000, parseInt(e.target.value) || 100)))}
              inputProps={{min: 1, max: 20000}}
              size="small"
              sx={{width: 120}}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={skipExistingUrls}
                onChange={e => setSkipExistingUrls(e.target.checked)}
              />
            }
            label={<Typography sx={{fontSize: 13, color: '#6B7280'}}>Skip documents already in library</Typography>}
            sx={{mt: 1.5}}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={skipClassification}
                onChange={e => setSkipClassification(e.target.checked)}
              />
            }
            label={<Typography sx={{fontSize: 13, color: '#6B7280'}}>Crawl only (skip classification & extraction)</Typography>}
            sx={{mt: 0.5}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStartCrawl}
            disabled={!seedUrl.trim() || starting}
          >
            {starting ? 'Starting...' : 'Start Crawl'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
